import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createDatasetItem,
  deleteDatasetItem,
  deleteDatasetItems,
  fetchDatasetItems,
  fetchDatasetItemsPaginated
} from "../../../data/queries/datasets";
import {
  CreateDatasetItemRequest,
  DatasetItemFilters,
  PaginationParams
} from "../../../types/dataset";

interface UseDatasetItemsOptions {
  datasetId: string;
  pagination?: PaginationParams;
  filters?: DatasetItemFilters;
  enabled?: boolean;
}

// Query keys factory for better cache management
const datasetItemKeys = {
  all: ["datasetItems"] as const,
  lists: () => [...datasetItemKeys.all, "list"] as const,
  list: (datasetId: string, filters?: DatasetItemFilters) =>
    [...datasetItemKeys.lists(), { datasetId, filters }] as const,
  paginated: (datasetId: string, pagination: PaginationParams, filters?: DatasetItemFilters) =>
    [...datasetItemKeys.all, "paginated", { datasetId, pagination, filters }] as const
};

export const useDatasetItems = (options: UseDatasetItemsOptions) => {
  const { datasetId, pagination, filters, enabled = true } = options;
  const queryClient = useQueryClient();

  // Query for all dataset items (non-paginated)
  const allItemsQuery = useQuery({
    queryKey: datasetItemKeys.list(datasetId, filters),
    queryFn: () => fetchDatasetItems(datasetId, filters),
    enabled: enabled && !!datasetId && !pagination,
    select: (data) => data.success ? data.data : [],
    throwOnError: false
  });

  // Query for paginated dataset items
  const paginatedItemsQuery = useQuery({
    queryKey: datasetItemKeys.paginated(datasetId, pagination!, filters),
    queryFn: () => fetchDatasetItemsPaginated(datasetId, pagination!, filters),
    enabled: enabled && !!datasetId && !!pagination,
    select: (data) => data.success ? data.data : null,
    throwOnError: false
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (itemData: CreateDatasetItemRequest) => createDatasetItem(itemData),
    onSuccess: (result) => {
      if (result.success) {
        // Invalidate and refetch dataset items for this dataset
        queryClient.invalidateQueries({
          queryKey: datasetItemKeys.all,
          predicate: (query) => {
            const queryKey = query.queryKey as any[];
            return queryKey.some(key =>
              typeof key === "object" && key?.datasetId === datasetId
            );
          }
        });
      }
    }
  });

  // Delete single item mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDatasetItem(id),
    onSuccess: (result) => {
      if (result.success) {
        // Just invalidate dataset item queries for this dataset
        queryClient.invalidateQueries({
          queryKey: datasetItemKeys.all,
          predicate: (query) => {
            const queryKey = query.queryKey as any[];
            return queryKey.some(key =>
              typeof key === "object" && key?.datasetId === datasetId
            );
          }
        });
      }
    }
  });

  // Delete multiple items mutation
  const deleteMultipleMutation = useMutation({
    mutationFn: (ids: string[]) => deleteDatasetItems(ids),
    onSuccess: (result) => {
      if (result.success) {
        // Just invalidate dataset item queries for this dataset
        queryClient.invalidateQueries({
          queryKey: datasetItemKeys.all,
          predicate: (query) => {
            const queryKey = query.queryKey as any[];
            return queryKey.some(key =>
              typeof key === "object" && key?.datasetId === datasetId
            );
          }
        });
      }
    }
  });

  // Determine which query to use based on pagination
  const activeQuery = pagination ? paginatedItemsQuery : allItemsQuery;

  // Extract items from the appropriate query
  const items = pagination
    ? (paginatedItemsQuery.data?.data || [])
    : (allItemsQuery.data || []);

  const paginatedData = pagination ? paginatedItemsQuery.data : null;

  // Handle errors from queries
  const queryError = activeQuery.error;
  const mutationError = createMutation.error || deleteMutation.error || deleteMultipleMutation.error;
  const error = queryError || mutationError;

  return {
    // Data
    items,
    paginatedData,

    // Loading states
    loading: activeQuery.isLoading || activeQuery.isFetching,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending || deleteMultipleMutation.isPending,

    // Error handling
    error: error ? (error instanceof Error ? error.message : String(error)) : null,

    // Actions
    createItem: createMutation.mutateAsync,
    deleteItem: deleteMutation.mutateAsync,
    deleteItems: deleteMultipleMutation.mutateAsync,

    // Utilities
    refreshItems: () => queryClient.invalidateQueries({
      queryKey: datasetItemKeys.all,
      predicate: (query) => {
        const queryKey = query.queryKey as any[];
        return queryKey.some(key =>
          typeof key === "object" && key?.datasetId === datasetId
        );
      }
    }),
    fetchItems: () => activeQuery.refetch()
  };
};
